// UI strings for "Ääntämisanalyysi", the panel shown after a recording (pronunciation/
// PronunciationAnalysis.tsx): the long-vowel meter, and room for the stress check. Finnish is the
// default and never truncated; en has exactly the same shape.

export const analysisFi = {
  title: 'Ääntämisanalyysi',
  lengthTitle: 'Vokaalien pituus',
  working: 'Mitataan äänitystä…',
  hint: 'Ääriviiva näyttää, kuinka pitkä vokaalin pitäisi olla sinun puhetahdissasi, täytetty palkki kuinka pitkä se oli. Mittaus on suuntaa antava vihje, ei arvosana.',
  calibrated:
    'Peterson on mallina. Hänkään ei venytä pitkiä vokaaleja yhtä pitkiksi kuin suomessa: hänen äänitteissään pitkä vokaali on keskimäärin vain noin 1,1-kertainen lyhyeen verrattuna. Mittari huomauttaa vain selvästä erosta häneen.',
  modeReference: 'Verrattu Petersonin omaan äänitteeseen tästä tekstistä.',
  modePeterson: 'Tästä tekstistä ei ole Petersonin äänitettä, joten mittari käyttää hänen keskimääräisiä pituuksiaan.',
  practiceTag: 'harjoitusvauhti',
  modePractice:
    'Mittari odottaa pitkät vokaalit tarkoituksella liioitellun pitkinä, noin 1,8-kertaisina lyhyeen verrattuna. Peterson itse ei venytä näin paljon. Harjoitusvauhdin saa pois Asetuksista.',
  compare: (vowel: string, his: string, yours: string) => `Peterson pitää tämän ${vowel}:n ${his}-kertaisena lyhyeen verrattuna, sinä ${yours}-kertaisena.`,
  /** Asetukset → Vastaukset: the Harjoitusvauhti switch, per person on this device. */
  setting: {
    label: 'Pitkien vokaalien mittari',
    off: 'Petersonin tahti',
    on: 'Harjoitusvauhti',
    exOff:
      'Petersonin tahti: mittari vertaa pituuksia Petersonin omaan äänitteeseen tai hänen keskimääräisiin pituuksiinsa. Esimerkiksi {word}-sanan ī saa olla vain vähän lyhyttä vokaalia pidempi. Yksittäistä sanaa harjoitellaan silti ensin harjoitusvauhdilla, sitten Petersonin tahtiin.',
    exOn: 'Harjoitusvauhti: mittari odottaa {word}-sanan ī:n liioitellun pitkänä, noin 1,8-kertaisena lyhyeen verrattuna. Kaikki äänityksesi mitataan näin, myös lauseet, eikä sanoilla ole luontevaa vaihetta. Koskee vain sinua tällä laitteella.',
  },
  legendExpected: 'odotettu pituus',
  legendYours: 'sinun äänityksesi',
  legendShort: 'selvästi liian lyhyt tai pitkä',
  legendUnsure: 'ei varmaa mittausta',
  pct: (n: number) => `${n}\u00a0%`,
  allGood: 'Pitkät vokaalit erottuivat lyhyistä.',
  noLong: 'Tekstissä ei ole pitkiä vokaaleja, ja lyhyet pysyivät lyhyinä.',
  someUnsure: 'Kaikista tavuista ei saa varmaa mittausta. Niistä mittari ei sano mitään.',
  lengthen: (vowel: string, finnishLong: string, longWord: string, shortWord: string) =>
    `Pidä ${vowel} pitkänä, kuten ${finnishLong} sanassa ”${longWord}” (ei ”${shortWord}”).`,
  shorten: (vowel: string, shortWord: string, longWord: string) => `Pidä ${vowel} lyhyenä, kuten sanassa ”${shortWord}” (ei ”${longWord}”).`,
  kindLong: 'pitkä vokaali',
  kindShort: 'lyhyt vokaali',
  kindDiphthong: 'diftongi',
  stressed: 'painollinen',
  measured: (pct: number) => `mitattu ${pct}\u00a0% odotetusta`,
  verdictShort: 'selvästi liian lyhyt',
  verdictLong: 'liian pitkä',
  verdictUnsure: 'ei varmaa mittausta',
  cannot: {
    silence: 'Äänityksessä ei kuulu puhetta.',
    noise: 'Taustalla on liikaa melua, joten pituuksia ei voi mitata. Kokeile hiljaisemmassa paikassa.',
    clipping: 'Ääni säröytyy. Pidä puhelinta vähän kauempana suusta.',
    'too-fast': 'Tavut menevät liian nopeasti yhteen. Sano teksti hitaasti ja selvästi.',
    'count-mismatch': 'Äänityksessä on eri määrä tavuja kuin tekstissä. Sanoitko koko tekstin etkä mitään muuta?',
    'unclear-syllables': 'Tavujen rajat eivät erotu selvästi. Sano teksti hitaasti, tavu kerrallaan.',
    'no-short-reference': 'Tekstissä ei ole lyhyttä vokaalia, johon pitkiä voisi verrata, joten pituuksia ei arvioida.',
    'too-short': 'Äänitys on liian lyhyt mitattavaksi.',
    'no-audio': 'Äänityksestä ei saatu ääntä.',
    'no-text': 'Tekstissä ei ole tavuja mitattavaksi.',
    default: 'Tästä äänityksestä ei saa luotettavaa mittausta.',
  } as Record<string, string>,
  unavailable: 'Tämä selain ei osaa avata äänitystä mitattavaksi.',
  failed: 'Mittaus ei onnistunut.',
};

export const analysisEn: typeof analysisFi = {
  title: 'Pronunciation check',
  lengthTitle: 'Vowel length',
  working: 'Measuring the recording…',
  hint: 'The outline shows how long the vowel should be at your speaking pace, the filled bar how long it was. The measurement is a rough hint, not a grade.',
  calibrated:
    'Peterson is the model. Even he does not stretch long vowels as much as Finnish does: in his recordings a long vowel is on average only about 1.1× a short one. The meter only points out a clear difference from him.',
  modeReference: 'Compared with Peterson’s own recording of this text.',
  modePeterson: 'Peterson has no recording of this text, so the meter uses his average lengths.',
  practiceTag: 'practice pace',
  modePractice:
    'The meter deliberately expects exaggerated long vowels, about 1.8× a short one. Peterson himself does not stretch them this much. Practice pace can be turned off in Settings.',
  compare: (vowel, his, yours) => `Peterson holds this ${vowel} ${his}× as long as a short vowel; you ${yours}×.`,
  setting: {
    label: 'Long-vowel meter',
    off: 'Peterson’s pace',
    on: 'Practice pace',
    exOff:
      'Peterson’s pace: the meter compares lengths with Peterson’s own recording or his average lengths. For example, the ī in {word} may be only a little longer than a short vowel. A single word is still practised at practice pace first, then at Peterson’s.',
    exOn: 'Practice pace: the meter expects the ī in {word} exaggerated, about 1.8× a short vowel. All your recordings are measured this way, sentences too, and words have no natural step. Applies only to you on this device.',
  },
  legendExpected: 'expected length',
  legendYours: 'your recording',
  legendShort: 'clearly too short or too long',
  legendUnsure: 'no reliable measurement',
  pct: (n) => `${n} %`,
  allGood: 'The long vowels stood out from the short ones.',
  noLong: 'There are no long vowels in this text, and the short ones stayed short.',
  someUnsure: 'Not every syllable could be measured reliably. The meter says nothing about those.',
  lengthen: (vowel, finnishLong, longWord, shortWord) => `Hold ${vowel} long, like ${finnishLong} in Finnish ”${longWord}” (not ”${shortWord}”).`,
  shorten: (vowel, shortWord, longWord) => `Keep ${vowel} short, as in Finnish ”${shortWord}” (not ”${longWord}”).`,
  kindLong: 'long vowel',
  kindShort: 'short vowel',
  kindDiphthong: 'diphthong',
  stressed: 'stressed',
  measured: (pct) => `measured ${pct} % of expected`,
  verdictShort: 'clearly too short',
  verdictLong: 'too long',
  verdictUnsure: 'no reliable measurement',
  cannot: {
    silence: 'There is no speech in the recording.',
    noise: 'There is too much background noise to measure lengths. Try somewhere quieter.',
    clipping: 'The sound is distorted. Hold the phone a little further from your mouth.',
    'too-fast': 'The syllables run together too fast. Say the text slowly and clearly.',
    'count-mismatch': 'The recording has a different number of syllables from the text. Did you say the whole text and nothing else?',
    'unclear-syllables': 'The syllable boundaries are not clear. Say the text slowly, one syllable at a time.',
    'no-short-reference': 'The text has no short vowel to compare the long ones with, so lengths are not judged.',
    'too-short': 'The recording is too short to measure.',
    'no-audio': 'No sound could be read from the recording.',
    'no-text': 'The text has no syllables to measure.',
    default: 'This recording cannot be measured reliably.',
  },
  unavailable: 'This browser cannot open the recording for measuring.',
  failed: 'The measurement failed.',
};
